/**
 * Documentation index — one array, two consumers.
 *
 * @remarks
 * The same rule `faq.ts` exists to demonstrate, applied to a list rather than a set of questions:
 * the array below is rendered as the cards on `DocumentationView.vue` and handed to
 * `seo.itemList()` in `entities.ts` to produce the route's `CollectionPage` and its `ItemList`.
 * An `ItemList` naming documents a visitor cannot see on the page is the same claim an `FAQPage`
 * makes about invisible questions, and rendering both from here is what makes that impossible.
 *
 * **The documents live in the repository, not on this origin.** They are Markdown under `docs/`,
 * published two other ways — HonKit produces the book, TypeDoc produces the API reference — and
 * neither output is part of this site. So each entry's `url` points at the file on the source host,
 * which is a URL that resolves today, rather than at a route this application does not serve.
 * {@link DocumentationConfig.source} is what makes that one value to change on a fork.
 *
 * **Relative imports only** — this file is in the configuration layer.
 * See AGENTS.md § The Thing That Breaks Silently.
 *
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * Config
 */
import { softwareConfig } from './software'

/**
 * A single document in the index.
 */
export interface DocumentationEntry {
  /** Stable key, used for the render key and to group the entry under a section heading. */
  readonly section: 'architecture' | 'guide' | 'reference'
  /** Title, exactly as rendered and as emitted into the `ItemList`. */
  readonly title: string
  /** What the document covers, in one line. Plain text — it is emitted verbatim into JSON-LD. */
  readonly description: string
  /**
   * The document summarised, one complete sentence per point.
   *
   * @remarks
   * Rendered on the page and joined into the `TechArticle`'s `abstract`, which is why each point is
   * a sentence rather than a fragment: a summary that only parses as a bullet list reads as broken
   * prose the moment it is concatenated.
   *
   * This is what a visitor gets without leaving the site. Keep it to what the document actually
   * says — a summary that promises something the file does not cover is the same broken claim as an
   * `ItemList` naming a document nobody can open.
   */
  readonly summary: readonly string[]
  /** Absolute URL of the document. Built from {@link DocumentationConfig.source}. */
  readonly url: string
}

/**
 * Shape of {@link documentationConfig}.
 */
export interface DocumentationConfig {
  /**
   * Where the documents are browsable, no trailing slash.
   *
   * @remarks
   * The repository's default branch on the source host. A fork that renames its branch, moves to
   * another host, or publishes the book somewhere public changes this one value; every entry below
   * and every `url` in the `ItemList` follows.
   */
  readonly source: string
  /**
   * The index the documentation tree publishes for itself.
   *
   * @remarks
   * `docs/README.md` is the table this page's entries are derived from, and the page links to it
   * rather than to `source` alone — a branch URL with no path is not a document.
   */
  readonly index: string
  /** The documents, in the order the page renders them. */
  readonly entries: readonly DocumentationEntry[]
}

/**
 * The repository's own documentation tree, on its default branch.
 *
 * @remarks
 * Derived from `softwareConfig.repository` rather than written out again: the source of the
 * software and the home of its documentation are the same repository, and two literals for one
 * origin is the pair that eventually disagrees.
 */
const source = `${softwareConfig.repository}/blob/master`

/**
 * The documentation index shown on the documentation route and mirrored into its `ItemList`.
 *
 * @remarks
 * The descriptions are the ones `docs/README.md` already publishes for the same files, so the two
 * indexes say the same thing about each document.
 *
 * @example
 * ```ts
 * import { documentationConfig } from '@/config/documentation'
 *
 * const guides = documentationConfig.entries.filter(entry => entry.section === 'guide')
 * ```
 */
export const documentationConfig: DocumentationConfig = {
  source,
  index: `${source}/docs/README.md`,
  entries: [
    {
      section: 'architecture',
      title: 'Architecture Overview',
      description:
        'The rendering pipeline, the layers and their enforced boundaries, how data flows, and what the build emits.',
      summary: [
        'Application code runs twice — once in Node during the build to produce each route as its own HTML file, and once in the browser to hydrate it.',
        'Four layers, and imports may only point one way through them: core, then the leaf layers, then services, then views and components.',
        'The build emits more than pages: the sitemap, robots file and manifest are produced from what was actually rendered, not from what was declared.'
      ],
      url: `${source}/docs/architecture/overview.md`
    },
    {
      section: 'guide',
      title: 'SEO',
      description:
        'How metadata reaches the prerendered HTML: titles, robots directives, the sitemap, share images and locales.',
      summary: [
        'A route entry carrying a title, a description and a schema array is the whole interface; the canonical URL, the Open Graph tags and the sitemap entry follow from it.',
        'Titles run through one template, robots directives fall back to a site-wide default, and the sitemap is generated from the emitted files rather than the route table.',
        'It also explains why switching locale in the browser is a UX feature rather than an indexing one: a crawler indexes the URL, and one URL cannot rank as two languages.'
      ],
      url: `${source}/docs/guide/seo.md`
    },
    {
      section: 'guide',
      title: 'Structured Data',
      description: 'The @id graph, the two rules that keep it valid, and how to add an entity without breaking them.',
      summary: [
        'Two rules hold the graph together: one @id per real-world entity, declared exactly once, and the site-wide entities on the home route only.',
        'Adding a page entity is walked through end to end, including when a site should declare a Person and when it should declare an Organization.',
        'The knowledge-panel section is the part most sites skip: registry identifiers and reciprocal profile links are what let a consumer reconcile the entity rather than merely read it.'
      ],
      url: `${source}/docs/guide/structured-data.md`
    },
    {
      section: 'guide',
      title: 'Deployment',
      description:
        'Cloudflare Pages as the default target, and the equivalent configuration for Netlify, Vercel, CloudFront and GitHub Pages.',
      summary: [
        'Cloudflare Pages is the default, either through the Git integration or a GitHub Actions job — one route or the other, never both.',
        'Netlify, Vercel, S3 with CloudFront and GitHub Pages each get their own equivalent of the headers, redirects and 404 document.',
        'Caching and environment variables close it out, since both are the parts that differ most between hosts.'
      ],
      url: `${source}/docs/guide/deployment.md`
    },
    {
      section: 'guide',
      title: 'Performance',
      description: 'The Core Web Vitals decisions this template starts from, and the reason behind each one.',
      summary: [
        'One section per metric — Largest Contentful Paint, Cumulative Layout Shift and Interaction to Next Paint — with the decision each one drove.',
        'Prerendered markup and self-hosted preloaded fonts are the two largest wins, and both are far harder to retrofit than to start with.',
        'Caching, the service worker, honouring a reduced-motion preference, and how to measure any of it are covered at the end.'
      ],
      url: `${source}/docs/guide/performance.md`
    },
    {
      section: 'guide',
      title: 'Local Development',
      description: 'The commands, how to add a route or a service, and what to check when something does not build.',
      summary: [
        'Setup is three commands, and the dev server deliberately does not prerender — which is why some problems only appear in a preview build.',
        'Adding a route and adding an API call each get a worked example, including why a view never calls fetch itself.',
        'The troubleshooting section starts with the import that resolves in development and fails only in a production build.'
      ],
      url: `${source}/docs/guide/local-development.md`
    },
    {
      section: 'reference',
      title: 'Agent Guide',
      description:
        'Conventions, the architecture rules ESLint enforces, the documentation standard, and the fork checklist.',
      summary: [
        'The module map and the naming rules, marking which of them are ESLint rules rather than conventions anyone has to remember.',
        'The documentation standard: which JSDoc tags to use, which take no argument, and which must never appear at all.',
        'A section on the failure that only shows up in CI, and a fork checklist in the order the files actually have to be changed.'
      ],
      url: `${source}/AGENTS.md`
    },
    {
      section: 'reference',
      title: 'Release Notes',
      description: 'What shipped in each version, and the changes-log entries the release was assembled from.',
      summary: [
        'Version 1.0.0 is the first working release: prerendered routes, the structured-data graph, generated sitemap and robots, and the Cloudflare Pages deployment.',
        'Each release records its features, architecture changes, fixes and known issues, plus how to upgrade from the version before it.',
        'The changes-log entries the notes were assembled from sit alongside them, dated, so a change can be traced to the day it landed.'
      ],
      url: `${source}/docs/release-notes/1.0.0.md`
    },
    {
      section: 'reference',
      title: 'Contributing',
      description: 'How to propose a change, and what a pull request is expected to carry before review.',
      summary: [
        'Discuss the change with the maintainers before making it — through an issue, email, or any other route.',
        'The pull request process covers build dependencies, keeping the README in step with the interface, and the SemVer bump the change implies.',
        'A pull request merges on two sign-offs, and the code of conduct applies to every interaction with the project.'
      ],
      url: `${source}/CONTRIBUTING.md`
    }
  ]
}
